import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { API } from '../../App';

/**
 * PlaidBackfillProgress
 * ---------------------
 * Shows backfill progress for a newly-connected bank. Polls the connection
 * row every 4s until backfill_status is "complete" or "error". Renders
 * nothing for connections that never started a backfill (legacy rows).
 *
 * Copy is provider-neutral.
 */
export default function PlaidBackfillProgress({ plaidConnections = [], onComplete }) {
  const [statuses, setStatuses] = useState({});

  // Track which connections we should poll
  const polled = plaidConnections.filter(
    (c) => c.backfill_status === 'requested' || c.backfill_status === 'in_progress'
  );

  useEffect(() => {
    if (polled.length === 0) return undefined;
    let active = true;
    let timer;

    const tick = async () => {
      try {
        const res = await axios.get(`${API}/plaid/connections`);
        if (!active) return;
        const next = {};
        (res.data || []).forEach((c) => {
          next[c.id] = c.backfill_status;
        });
        setStatuses(next);
        // Has anything finished?
        const finishedIds = polled
          .map((c) => c.id)
          .filter((id) => next[id] === 'complete' || next[id] === 'error');
        if (finishedIds.length > 0 && onComplete) {
          onComplete();
        }
      } catch (e) {
        // silent - banner just stays until polling resumes
      }
      timer = setTimeout(tick, 4000);
    };
    tick();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polled.map((c) => c.id).join(',')]);

  const inProgress = plaidConnections.filter((c) => {
    const s = statuses[c.id] ?? c.backfill_status;
    return s === 'requested' || s === 'in_progress';
  });
  const completedJustNow = plaidConnections.filter((c) => {
    const s = statuses[c.id];
    return s === 'complete' && (c.backfill_status === 'requested' || c.backfill_status === 'in_progress');
  });
  const errored = plaidConnections.filter((c) => {
    const s = statuses[c.id] ?? c.backfill_status;
    return s === 'error';
  });

  if (inProgress.length === 0 && completedJustNow.length === 0 && errored.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6">
      {inProgress.length > 0 && (
        <div
          data-testid="plaid-backfill-in-progress"
          className="border border-blue-300 bg-blue-50 rounded-lg p-4 flex items-start gap-3"
        >
          <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0 animate-spin" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-blue-900">Importing your transaction history</p>
            <p className="text-sm text-blue-800 mt-0.5">
              We&apos;re fetching up to 24 months of transactions from{' '}
              {inProgress.map((c) => c.institution_name).join(', ')}. This usually takes 1–3
              minutes; you can keep using AkountFly while it runs.
            </p>
          </div>
        </div>
      )}

      {completedJustNow.length > 0 && (
        <div
          data-testid="plaid-backfill-complete"
          className="border border-emerald-300 bg-emerald-50 rounded-lg p-4 flex items-start gap-3"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-emerald-900">Transaction history imported</p>
            <p className="text-sm text-emerald-800 mt-0.5">
              {completedJustNow.map((c) => c.institution_name).join(', ')}:{' '}
              {completedJustNow.reduce((acc, c) => acc + (c.transactions_imported || 0), 0)} transactions
              ready for review.
            </p>
          </div>
        </div>
      )}

      {errored.length > 0 && (
        <div
          data-testid="plaid-backfill-error"
          className="border border-rose-300 bg-rose-50 rounded-lg p-4 flex items-start gap-3"
        >
          <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-rose-900">Could not import transaction history</p>
            <p className="text-sm text-rose-800 mt-0.5">
              We hit an error fetching transactions for{' '}
              {errored.map((c) => c.institution_name).join(', ')}. Click &quot;Sync now&quot; on the
              bank tile to retry. If it keeps failing, please reconnect the bank.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
